import React, { FC } from 'react';

import { AlarmType } from '@thingslog/repositories';

import { AlarmChipSize } from './AlarmChipSize';

import { cn } from '../Utils/cnUtil';

/**
 * Turns a backend alarm type constant into readable text: `EMPTY_SPOOL` -> `Empty spool`.
 * Exported so callers can keep a column's filter values in step with what the cells render.
 */
export const humanizeAlarmType = (alarmType: string): string => {
  const words = alarmType.replace(/_/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * Translation key for a backend alarm type constant: `EMPTY_SPOOL` -> `alarm_type_empty_spool`.
 * Lives here rather than in an app so portal and management resolve the same key; pair it with a
 * `defaultValue` of `humanizeAlarmType` so a type added server-side still renders readable text.
 */
export const alarmTypeTranslationKey = (alarmType: string): string =>
  `alarm_type_${alarmType.toLowerCase()}`;

export const UNKNOWN_ALARM_TYPE_COLOR = '#9ca3af';

/**
 * One colour per alarm type — 34 distinct values, mirroring `AlarmType` in thingslog-server
 * (thingslog-models, com.thingslog.servers.types). Related alarms sit in neighbouring hues so a
 * table still reads by family, while no two types look alike.
 *
 * Colour is carried by the dot alone: a tinted pill per type turned a table of alarms into a wall
 * of colour. Plain hex rather than Tailwind classes so non-Tailwind consumers (the management
 * dashboard's pie chart) colour the same type identically.
 */
export const ALARM_TYPE_COLORS: Record<AlarmType, string> = {
  // Flow
  LOW_FLOW: '#38bdf8',
  HIGH_FLOW: '#0284c7',
  NEGATIVE_FLOW: '#3b82f6',
  REVERSE_FLOW: '#1d4ed8',
  // Consumption
  NO_ZERO_CONSUMPTION: '#22d3ee',
  ALL_ZERO_CONSUMPTION: '#0891b2',
  HIGH_CONSUMPTION: '#14b8a6',
  NO_CONSUMPTION: '#0f766e',
  // Level
  LOW_LEVEL: '#818cf8',
  HIGH_LEVEL: '#4f46e5',
  VERY_LOW_LEVEL: '#8b5cf6',
  VERY_HIGH_LEVEL: '#6d28d9',
  // Tampering & integrity
  TAMPER: '#f43f5e',
  SENSOR_FRAUD: '#be123c',
  MAGNETIC_FIELD: '#d946ef',
  DEVICE_REMOVED: '#db2777',
  // Device faults
  RESET_ERROR: '#fb923c',
  RF_ERROR: '#ea580c',
  CS_ERROR: '#ef4444',
  MEASURING_ERROR: '#b91c1c',
  // Physical / hydraulic
  BLOCKAGE: '#f59e0b',
  BURST: '#b45309',
  LEAKAGE: '#eab308',
  EMPTY_SPOOL: '#84cc16',
  STANDSTILL: '#4d7c0f',
  // Connectivity & power
  MISSED_TRANSMISSION: '#a855f7',
  LOW_BATTERY: '#7e22ce',
  // Sizing
  OVERSIZED: '#10b981',
  UNDERSIZED: '#047857',
  // Environment
  LOW_TEMPERATURE: '#075985',
  // General / catch-all
  ON_OFF_ALARM: '#64748b',
  RANGE_EVENT: '#52525b',
  COMBINATION_OF_ALARMS: '#78716c',
  RESET_EVENT: '#6b7280',
};

/**
 * Type tag for an alarm: a colour-coded dot next to plain text, with no pill of its own. Keeps the
 * type identifiable at a glance without adding a second block of colour beside the severity and
 * status chips.
 */
const AlarmTypeChip: FC<AlarmTypeChipProps> = ({
  className,
  alarmType,
  label,
  size = 'sm',
}: AlarmTypeChipProps) => {
  const text = label ?? humanizeAlarmType(alarmType);

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap',
        'font-medium leading-5 text-gray-700',
        size === 'md' ? 'text-sm' : 'text-xs',
        className
      )}
      title={text}
    >
      <span
        className={cn('shrink-0 rounded-full', size === 'md' ? 'h-2.5 w-2.5' : 'h-2 w-2')}
        style={{ backgroundColor: ALARM_TYPE_COLORS[alarmType] ?? UNKNOWN_ALARM_TYPE_COLOR }}
      />
      {text}
    </span>
  );
};

interface AlarmTypeChipProps {
  className?: string;
  /** Raw alarm type constant, e.g. `EMPTY_SPOOL`. */
  alarmType: AlarmType;
  /** Overrides the humanized text — pass a translated label when one exists. */
  label?: string;
  size?: AlarmChipSize;
}

export default AlarmTypeChip;
